import { Arr } from '../../../CodesForFoundation';
import { Component, ObjectManager } from '../../../concepts/objects';
import { Controller, resolve } from '../../Container';
import { AdapterInfo } from '../AdapterInfo';
import { EditorSession } from '../EditorSession';
import { Physical } from '../entities/Physical';
import { PointerInfo } from '../PointerInfo';
import { RootOverride } from '../RootOverride';
import { Selection, SelectionSet } from '../Selection';
import { Pose, PoseAttr } from './Pose';

export class PoseMechanics extends Controller {

    @resolve(ObjectManager) objects!: ObjectManager;
    @resolve(EditorSession) session!: EditorSession;

    public start(): void {
        this.burden(this.session.observe(AdapterInfo, this.adapterDidChange));
        this.burden(this.session.observe(PointerInfo, this.pressedDidChange));
        this.burden(this.session.observe(PointerInfo, this.targetsDidChange));
        this.burden(this.session.observe(RootOverride, this.rootDidChange));
        this.burden(this.session.observe(Selection, this.selectionDidChange));
    }

    private adapterDidChange = (newState: AdapterInfo | null, oldState: AdapterInfo | null) => {
        if (newState !== oldState) {
            if (oldState.target !== 'none') {
                this.apply(oldState.target, { beingEdited: false });
            }
            if (newState.target !== 'none') {
                this.apply(newState.target, { beingEdited: true });
            }
        }
    };

    private rootDidChange = (newState: Physical | null, oldState: Physical | null) => {
        if (newState !== oldState) {
            if (oldState) {
                this.apply(oldState.id, { isRoot: false });
            }
            if (newState) {
                this.apply(newState.id, { isRoot: true });
            }
        }
    };

    private selectionDidChange = (newState: SelectionSet, oldState: SelectionSet) => {
        // Any that are no longer selected, update
        for (const p of oldState) {
            if (!newState.has(p)) {
                this.apply(p, { selected: false, peers: false });
            }
        }
        // Any that are selected, update if applicable
        for (const p of newState) {
            if (!oldState.has(p) || oldState.size === 1) {
                this.apply(p, { selected: true, peers: newState.size > 1, });
            }
        }
    };

    private targetsDidChange = (newState: PointerInfo, oldState: PointerInfo) => {
        const next = Arr.first(newState.targets);
        const prev = Arr.first(oldState.targets);
        // If the top target is now different, update (obviously only components)
        if (next !== prev) {
            if (next instanceof Component) {
                this.apply(next, { targeted: true });
            }
            if (prev instanceof Component) {
                this.apply(prev, { targeted: false, interaction: null });
            }
        }
    };

    private pressedDidChange = (newState: PointerInfo, oldState: PointerInfo) => {
        const next = newState.pressed;
        const prev = oldState.pressed;
        // If the pressed is now different, update (obviously only components)
        if (next !== prev) {
            if (next instanceof Component) {
                this.apply(next, { pressed: true });
            }
            if (prev instanceof Component) {
                this.apply(prev, { pressed: false });
            }
        }
    };

    private apply(id: string | Component, patch: Partial<Pose>) {
        // Do this defensively;
        const subject = typeof (id) === 'string' ? this.objects.getMaybe(id) : id;
        if (subject && subject.alive) {
            const curr = PoseAttr.get(subject);
            const next = { ...curr, ...patch };
            PoseAttr.set(subject, next);
        }
    }
}
